import React from 'react';
import {
  shouldShowLanguageBadge,
  getLanguageName,
  getLanguageBadgeClasses,
  formatLanguageCode,
} from '../../utils/language';
import { formatFullDate } from '../../utils/dateUtils';

export const tabs = [
  {
    id: 'summary',
    label: 'Summary',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
  },
  {
    id: 'content',
    label: 'Content',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
      </svg>
    ),
  },
  {
    id: 'original',
    label: 'Original',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
      </svg>
    ),
  },
];

export const ArticleHeaderBar = ({
  onGoBack,
  highlights,
  onOpenHighlights,
  currentArticle,
  isUpdating,
  isDeleting,
  onMarkAsRead,
  onMarkAsUnread,
  onDelete,
}) => (
  <div className="bg-surface border-b-3 border-clay-border shadow-clay-sm">
    <div className="max-w-4xl mx-auto px-2 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between py-4">
        <button
          onClick={onGoBack}
          className="btn-clay-ghost flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>

        <div className="flex items-center space-x-2">
          {/* Highlights button */}
          <button
            onClick={onOpenHighlights}
            className="relative p-2.5 text-secondary-text hover:text-fun-lemon hover:bg-amber-50 dark:hover:bg-amber-900/30
              rounded-clay border-2 border-transparent hover:border-fun-lemon
              transition-all duration-200"
            title="View highlights"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
            </svg>
            {highlights && highlights.length > 0 && (
              <span className="absolute -top-1 -right-1 badge-fun-lemon text-xs font-bold
                w-5 h-5 flex items-center justify-center animate-bounce-gentle">
                {highlights.length}
              </span>
            )}
          </button>

          {/* Done/Undone button */}
          <button
            onClick={currentArticle.is_read ? onMarkAsUnread : onMarkAsRead}
            disabled={isUpdating}
            className={`p-2.5 rounded-clay border-2 transition-all duration-200 ${
              currentArticle.is_read
                ? 'text-accent-600 border-accent-300 hover:bg-accent-50 hover:border-accent-400'
                : 'text-fun-mint border-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 hover:border-emerald-400'
            }`}
            title={currentArticle.is_read ? "Mark as unread" : "Mark as read"}
          >
            {isUpdating ? (
              <div className="spinner-fun-sm w-5 h-5"></div>
            ) : currentArticle.is_read ? (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </button>

          {/* Delete button */}
          <button
            onClick={onDelete}
            disabled={isDeleting}
            className="p-2.5 text-tertiary-text hover:text-fun-coral hover:bg-rose-50 dark:hover:bg-rose-900/30
              rounded-clay border-2 border-transparent hover:border-fun-coral
              transition-all duration-200"
            title="Delete article"
          >
            {isDeleting ? (
              <div className="spinner-fun-sm w-5 h-5"></div>
            ) : (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  </div>
);

export const ErrorBanner = ({ error, onDismiss }) => (
  <div className="bg-rose-50 dark:bg-rose-950/30 border-b-3 border-fun-coral">
    <div className="max-w-4xl mx-auto px-2 sm:px-6 lg:px-8 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xl" role="img" aria-hidden="true">😅</span>
          <p className="text-sm font-medium text-rose-800 dark:text-rose-200">{error}</p>
        </div>
        <button
          onClick={onDismiss}
          className="p-1 text-rose-600 hover:text-rose-800 hover:bg-rose-100 dark:hover:bg-rose-800/30 rounded-clay transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  </div>
);

const getDomainFromUrl = (url) => {
  try {
    return new URL(url).hostname.replace('www.', '');
  } catch {
    return 'Unknown source';
  }
};

export const ArticleMetadata = ({ article, readingTime }) => (
  <div className="bg-surface border-b-3 border-clay-border">
    <div className="max-w-4xl mx-auto px-2 sm:px-6 lg:px-8 py-8">
      <div className="mb-4">
        <div className="flex flex-wrap items-center text-sm text-secondary-text gap-2 mb-2">
          <span className="badge-fun-lavender">
            {getDomainFromUrl(article.url)}
          </span>
          {article.is_read && (
            <span className="badge-fun-mint flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Read
            </span>
          )}
          {shouldShowLanguageBadge(article.language) && (
            <span
              className={`${getLanguageBadgeClasses(article.language)}`}
              title={`Article language: ${getLanguageName(article.language)}`}
            >
              {formatLanguageCode(article.language)}
            </span>
          )}
          <time dateTime={article.publish_date || article.created_at} className="text-secondary-text">
            {formatFullDate(article.publish_date || article.created_at)}
          </time>
          {readingTime > 0 && (
            <>
              <span className="text-tertiary-text">•</span>
              <span className="text-secondary-text">{readingTime} min read</span>
            </>
          )}
        </div>
      </div>

      <h1 className="font-display text-3xl font-bold text-heading mb-4">
        {article.title || 'Untitled Article'}
      </h1>

      {(article.author || article.domain || article.publish_date || article.created_at) && (
        <div className="flex flex-wrap items-center text-sm text-secondary-text mb-4 gap-4">
          {article.author && (
            <div className="flex items-center">
              <svg className="w-4 h-4 mr-1.5 text-tertiary-text" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span>{article.author}</span>
            </div>
          )}
          {article.domain && (
            <div className="flex items-center">
              <svg className="w-4 h-4 mr-1.5 text-tertiary-text" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              <span>{article.domain}</span>
            </div>
          )}
          {(article.publish_date || article.created_at) && (
            <div className="flex items-center">
              <svg className="w-4 h-4 mr-1.5 text-tertiary-text" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span>{formatFullDate(article.publish_date || article.created_at)}</span>
            </div>
          )}
        </div>
      )}

      {/* Status indicator */}
      <div className="flex items-center mb-4">
        {article.status === 'processing' && (
          <div className="inline-flex items-center px-3 py-1.5 bg-amber-50 dark:bg-amber-950/30 border-2 border-fun-lemon rounded-clay">
            <div className="spinner-fun-sm w-4 h-4 mr-2"></div>
            <span className="text-sm font-medium text-amber-700 dark:text-amber-300">Processing article...</span>
          </div>
        )}
        {article.status === 'failed' && (
          <div className="inline-flex items-center px-3 py-1.5 bg-rose-50 dark:bg-rose-950/30 border-2 border-fun-coral rounded-clay">
            <span className="mr-2" role="img" aria-hidden="true">😅</span>
            <span className="text-sm font-medium text-rose-700 dark:text-rose-300">Failed to process article</span>
          </div>
        )}
      </div>
    </div>
  </div>
);

export const TabBar = ({ activeTab, onTabChange }) => (
  <div className="bg-surface border-b-3 border-clay-border">
    <div className="max-w-4xl mx-auto px-2 sm:px-6 lg:px-8">
      <nav className="flex space-x-2 py-3">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            className={`pill-clay flex items-center gap-2 ${
              activeTab === tab.id
                ? 'pill-clay-active'
                : ''
            }`}
          >
            {tab.icon}
            <span>{tab.label}</span>
          </button>
        ))}
      </nav>
    </div>
  </div>
);
